import {
  GROUP_ROUTES,
  buildOrganizationUsersUrl,
} from '@app/group/config/pages';
import { serializeGroupFilters } from '@app/group/hooks/use-group-filters';
import { GroupOrganizationList } from '@shared/api/types';
import Text from '@shared/components/content/text';
import { DataWithDrillthroughCell } from '@shared/components/data-table/cells/data-with-drillthrough-cell';
import { DateCell } from '@shared/components/data-table/cells/date-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { ColumnDef } from '@tanstack/react-table';

/**
 * Drillthrough target with the row's org applied as the global org filter and
 * the current period selection carried over. Serialised with the same nuqs
 * parsers useGroupFilters reads, so the target page picks the filters up
 * directly from the URL.
 */
function buildDrillUrl(
  route: string,
  orgUuid: string,
  periods: string[]
): string {
  return serializeGroupFilters(route, { orgs: [orgUuid], periods });
}

interface UseColumnsArgs {
  periods: string[];
}

export function useGroupOrganizationsColumns({
  periods,
}: UseColumnsArgs): ColumnDef<GroupOrganizationList>[] {
  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: ({ column }) => (
        <HeaderCell column={column} name="Organisation" />
      ),
      cell: ({ getValue }) => <Text>{getValue<string>()}</Text>,
      enableColumnFilter: true,
      meta: {
        label: 'Organisation',
        variant: 'text',
        placeholder: 'Search organisations…',
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'signupDate',
      accessorKey: 'signupDate',
      header: ({ column }) => <HeaderCell column={column} name="Signed up" />,
      cell: ({ getValue }) => <DateCell date={getValue<string>()} />,
    },
    {
      id: 'userCount',
      accessorKey: 'userCount',
      header: ({ column }) => (
        <HeaderCell column={column} name="Users" numberColumn />
      ),
      cell: ({ getValue, row }) => {
        const count = getValue<number>();
        if (!count)
          return <p className="pr-[18px] text-right text-gray-500">-</p>;
        return (
          <DataWithDrillthroughCell
            link={buildOrganizationUsersUrl(
              row.original.uuid,
              row.original.name
            )}
            ariaLabel={`View ${row.original.name} users`}
          >
            <NumberCell number={count} rounded className="text-teal-600" />
          </DataWithDrillthroughCell>
        );
      },
    },
    {
      id: 'publishedReportCount',
      accessorKey: 'publishedReportCount',
      header: ({ column }) => (
        <HeaderCell column={column} name="Company reports" numberColumn />
      ),
      cell: ({ getValue, row }) => {
        const count = getValue<number>();
        if (!count)
          return <p className="pr-[18px] text-right text-gray-500">-</p>;
        return (
          <DataWithDrillthroughCell
            link={buildDrillUrl(
              GROUP_ROUTES.COMPANY_REPORTS,
              row.original.uuid,
              periods
            )}
            ariaLabel={`View ${row.original.name} company reports`}
          >
            <NumberCell number={count} rounded className="text-teal-600" />
          </DataWithDrillthroughCell>
        );
      },
    },
    {
      id: 'purchasedGoodsItemCount',
      accessorKey: 'purchasedGoodsItemCount',
      header: ({ column }) => (
        <HeaderCell column={column} name="Purchased goods" numberColumn />
      ),
      cell: ({ getValue, row }) => {
        const count = getValue<number>();
        if (!count)
          return <p className="pr-[18px] text-right text-gray-500">-</p>;
        return (
          <DataWithDrillthroughCell
            link={buildDrillUrl(
              GROUP_ROUTES.PURCHASING_FOOD_AND_DRINK,
              row.original.uuid,
              periods
            )}
            ariaLabel={`View ${row.original.name} purchased goods`}
          >
            <NumberCell number={count} rounded className="text-teal-600" />
          </DataWithDrillthroughCell>
        );
      },
    },
    {
      id: 'productsCount',
      accessorKey: 'productsCount',
      header: ({ column }) => (
        <HeaderCell column={column} name="Products" numberColumn />
      ),
      cell: ({ getValue, row }) => {
        const count = getValue<number>();
        if (!count)
          return <p className="pr-[18px] text-right text-gray-500">-</p>;
        return (
          <DataWithDrillthroughCell
            link={buildDrillUrl(
              GROUP_ROUTES.PRODUCTS,
              row.original.uuid,
              periods
            )}
            ariaLabel={`View ${row.original.name} products`}
          >
            <NumberCell number={count} rounded className="text-teal-600" />
          </DataWithDrillthroughCell>
        );
      },
    },
  ];
}
